import {SyncClient, type SyncState, type SyncResult} from './sync-client.js';

export interface VerifiedTenant { organizationId: string; token: string; role: 'owner' | 'admin'; plan?: string; capacity?: number; maxHosts?: number }

/** Verify account authorization before opening a tenant data context. */
export async function verifyTenantSession(token: string, transport: typeof fetch = fetch): Promise<VerifiedTenant> {
  if (!/^[A-Za-z0-9_.-]{20,8192}$/.test(token)) throw new Error('Sesiune invalidă');
  const response = await transport('https://vorbello.eu/wp-json/vorbello/v1/client/service-access', {
    method:'GET', headers:{Authorization:`Bearer ${token}`}, cache:'no-store', credentials:'omit'
  });
  if (!response.ok) throw new Error('Sesiunea nu a putut fi verificată');
  const data = await response.json();
  if (data?.success !== true || data?.service_allowed !== true || !['owner','admin'].includes(data?.role) ||
      typeof data?.organization_id !== 'string' || !/^(?:license-[1-9][0-9]{0,18}|platform-1)$/.test(data.organization_id)) {
    throw new Error('Contul nu are acces la datele organizației');
  }
  const identity: VerifiedTenant = {organizationId:data.organization_id, token, role:data.role};
  if (typeof data.plan === 'string') identity.plan = data.plan;
  if (Number.isInteger(data.capacity) && data.capacity >= 0) identity.capacity = data.capacity;
  if (Number.isInteger(data.max_hosts) && data.max_hosts >= 0) identity.maxHosts = data.max_hosts;
  return identity;
}

/** Only pass state already cleared or scoped to the verified organization. */
export function createTenantSync(session: VerifiedTenant, read: () => SyncState,
  apply: (state: SyncState, metadata: any) => void, storage: Pick<Storage,'getItem'|'setItem'>,
  endpoint: string, transport: typeof fetch = fetch, status: (result: SyncResult) => void = () => {}): SyncClient {
  return new SyncClient(read, apply, storage, endpoint, transport, status, session);
}

/** Serializes account transitions and ignores late verification of a previous login. */
export class TenantSessionLifecycle {
  private generation = 0;
  private current: SyncClient | null = null;

  constructor(private clearVisibleState: () => void) {}

  async open(token: string, verify: (token: string) => Promise<VerifiedTenant>,
    create: (identity: VerifiedTenant) => SyncClient): Promise<VerifiedTenant> {
    const generation = ++this.generation;
    this.current?.dispose();
    this.current = null;
    this.clearVisibleState();
    const identity = await verify(token);
    if (generation !== this.generation) throw new Error('Sesiunea a fost schimbată');
    this.current = create(identity);
    return identity;
  }

  close(): void {
    ++this.generation;
    this.current?.dispose();
    this.current = null;
    this.clearVisibleState();
  }
}
